import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import { useContext, useEffect, useState, type ComponentType } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import gscApi, {
  useDeleteGscHistoryMutation,
  useGetUserGscHistoryQuery,
  useLazyGetHostNamesQuery
} from '@/app/KeywordResearch/api/gscApi';
import type {
  GscHistoryData,
  GscHistoryItem
} from '@/app/KeywordResearch/types/gscApiTypes';
import { gscHistoryColumnConfig } from '@/app/KeywordResearch/config/gscHistoryColumnConfig';
import KeywordGscForm from '../components/keyword-gsc/KeywordGscForm';
import Lottie from 'lottie-react';
import LottieLoading from '@/assets/LottieLoading.json';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import IconAlert from '@/components/common/IconAlert';
import ConnectionNameModal from '../components/keyword-gsc/ConnectionNameModal';
import { RemoveGscContext } from '@/context/Context';
import CancelModal from '@/app/common/components/CancelModal';
import { errorMessage, successMessage } from '@/utils/toast';
import { useDispatch } from 'react-redux';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type RemoveGscContextValue = {
  domain: string;
  setDomain: (domain: string) => void;
  account: string;
  setAccount: (account: string) => void;
  hostNameId: string;
  setHostNameId: (hostNameId: string) => void;
  show: boolean;
  setShow: (show: boolean) => void;
};

type AgGridWrapperProps = {
  columns: unknown[];
  data: unknown[];
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const getGscHistoryGridData = (
  data: GscHistoryData | undefined
): GscHistoryItem[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.status === 'not data' || data.status === 'not found host_name') {
    return [];
  }
  return [];
};

const getGscHistoryStatus = (data: GscHistoryData | undefined) =>
  data && !Array.isArray(data) ? data.status : undefined;

function KeywordsGsc() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [show, setShow] = useState(false);
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();

  useEffect(() => {
    setBreadcrumbData([{ text: 'کلمات GSC', href: '#' }]);
  }, [setBreadcrumbData]);

  const {
    data: gscHistory,
    isLoading: isLoadingHistory,
    isError: isErrorGetHistory,
    error: historyError,
    refetch: refetchHistory,
    isFetching: isFetchingHistory,
  } = useGetUserGscHistoryQuery();
  const [
    ,
    {
      isError: isErrorHostNames,
      error: hostNamesError,
      refetch: refetchHostNames,
      isFetching: isFetchingHostNames,
    },
  ] = useLazyGetHostNamesQuery();
  const {
    hostNameId,
    show: showRemoveHistory,
    setShow: setShowRemoveHistory,
    account,
    domain
  } = useContext(RemoveGscContext) as RemoveGscContextValue;
  const [deleteHistory] = useDeleteGscHistoryMutation();
  const dispatch = useDispatch();

  const historyData = gscHistory?.data;
  const historyStatus = getGscHistoryStatus(historyData);

  const queryError = getActiveQueryError([
    {
      isError: isErrorGetHistory,
      error: historyError,
      refetch: refetchHistory,
      isFetching: isFetchingHistory,
    },
    {
      isError: isErrorHostNames,
      error: hostNamesError,
      refetch: refetchHostNames,
      isFetching: isFetchingHostNames,
    },
  ]);

  if (queryError) return <DataLoadError {...queryError} />;

  return (
    <>
      <CancelModal
        submitTitle="آره، حذف بشه"
        desc={`آیا از حذف تاریخچه دامنه ${domain} مطمئن هستی؟`}
        setShow={setShowRemoveHistory}
        show={showRemoveHistory}
        submitFunc={() => {
          deleteHistory({
            host_name_id: hostNameId,
            account
          })
            .unwrap()
            .then(() => {
              successMessage(' عملیات با موفقیت انجام شد.');
              dispatch(gscApi.util.invalidateTags(['userGscHistory']));
            })
            .catch(err => {
              console.error('Error fetching data:', err);
              errorMessage('در انجام عملیات خطایی رخ داد.');
            });
        }}
        title="حذف تاریخچه GSC"
      />
      {searchParams.get('status') === 'ok' ? (
        <IconAlert
          onClose={() => setSearchParams('')}
          className="my-2"
          dismissible
          variant="success"
        >
          اتصال شما به کنسول گوگل با موفقیت انجام شد.
        </IconAlert>
      ) : searchParams.get('status') === 'no_domains' ? (
        <IconAlert
          onClose={() => setSearchParams('')}
          className="my-2"
          dismissible
          variant="warning"
        >
          اکانت شما اجازه دسترسی به هیچ سایتی را در سرچ کنسول گوگل ندارد.
        </IconAlert>
      ) : searchParams.get('status') === 'access_denied' ? <IconAlert
        onClose={() => setSearchParams('')}
        className="my-2"
        dismissible
        variant="warning"
      >
        عملیات اتصال به سرچ کنسول توسط کاربر لغو شد.
      </IconAlert> :searchParams.get('status') === 'nok' ? <IconAlert
        onClose={() => setSearchParams('')}
        className="my-2"
        dismissible
        variant="warning"
      >
        در عملیات اتصال به سرچ کنسول گوگل خطایی رخ داده است.
      </IconAlert> : null}

      {searchParams.get('status') === 'googlefail' && (
        <IconAlert
          onClose={() => setSearchParams('')}
          className="my-2"
          dismissible
          variant="danger"
        >
          از سمت گوگل خطای 403 ارسال شده لطفا دوباره تلاش کنید.
        </IconAlert>
      )}
      {!isLoadingHistory && (
        <>
          {historyStatus === 'not found account' ? (
            <div className="justify-content-center align-items-center my-3">
              <ConnectionNameModal show={show} setShow={setShow} />
              <div className="d-flex justify-content-center w-100 mb-3">
                <Button
                  variant="falcon-primary"
                  onClick={() => {
                    setShow(true);
                  }}
                  className="border-300 d-flex align-items-center"
                >
                  <>
                    <FontAwesomeIcon icon="link" />
                    <span className="ms-1">اتصال به سرچ کنسول گوگل (GSC)</span>
                  </>
                </Button>
              </div>
              <div
                className="mb-3 text-center"
                style={{ width: '250px', margin: 'auto' }}
              >
                <Lottie animationData={LottieLoading} />
              </div>
              <div className="mt-6 fw-medium text-center">
                برای شروع گرفتن کلمات، باید میزفاتولز رو به سرچ کنسول متصل کنی.
              </div>
            </div>
          ) : (
            <>
              <KeywordGscForm
                noHostName={historyStatus === 'not found host_name'}
              />
              <Row>
                <Col>
                  <div className="for-style">
                    <TypedAgGridWrapper
                      columns={gscHistoryColumnConfig}
                      data={getGscHistoryGridData(historyData)}
                    />
                  </div>
                  <style>
                    {`
                .for-style .ag-row-focus {
                    z-index: 2;
                }`}
                  </style>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </>
  );
}

export default KeywordsGsc;
